<script>
  import { Router, Route } from 'svelte-routing';
  import { onMount } from 'svelte';
  import { getToken, fetchMe, clearAuth, consumeTokenFromCallback, getUser, rememberReturnTo } from './lib/auth.js';
  import Captures from './routes/Captures.svelte';
  import CaptureDetail from './routes/CaptureDetail.svelte';
  import Buckets from './routes/Buckets.svelte';
  import BucketDetail from './routes/BucketDetail.svelte';
  import Audit from './routes/Audit.svelte';
  import Logs from './routes/Logs.svelte';
  import Smokescreen from './routes/Smokescreen.svelte';
  import Status from './routes/Status.svelte';
  import Sftp from './routes/Sftp.svelte';
  import SftpBucketDetail from './routes/SftpBucketDetail.svelte';
  import Settings from './routes/Settings.svelte';
  import Users from './routes/Users.svelte';
  import Callback from './routes/Callback.svelte';
  import Login from './routes/Login.svelte';
  import NoAccess from './routes/NoAccess.svelte';
  import Welcome from './routes/Welcome.svelte';
  import HomeRedirect from './routes/HomeRedirect.svelte';
  import AppShell from './components/AppShell.svelte';
  import Splash from './components/Splash.svelte';
  import UiHost from './components/UiHost.svelte';

  let user = $state(null);
  let booting = $state(true);

  onMount(async () => {
    if (window.location.pathname !== '/auth/callback') {
      consumeTokenFromCallback();
    }
    if (getToken()) {
      // Always validate the token against /auth/me at boot. The cached user
      // from localStorage is just for snappy first paint -- if the token
      // expired while the tab was closed, fetchMe clears it so the Welcome
      // page renders the signed-out state. On not_yet_valid the token is
      // kept (transient clock skew) and we use the cached user as a stopgap
      // so the SPA still renders; a later API call will revalidate.
      const r = await fetchMe();
      if (r.user) {
        user = r.user;
      } else if (r.code === 'not_yet_valid') {
        user = getUser();
      } else {
        user = null;
      }
    }
    // Public paths for unauthenticated users: only / (Welcome), /login
    // (which bounces to login.telebroad.com), and /auth/callback. Anything
    // else -- deep links, bookmarks, /captures after the token expires --
    // is sent through /login. We snapshot the original URL first so the
    // callback page can return the user here after they sign back in.
    if (!user) {
      const p = window.location.pathname;
      if (p !== '/' && p !== '/login' && p !== '/auth/callback') {
        rememberReturnTo();
        window.location.replace('/login');
        return;
      }
    }
    booting = false;
  });

  // Browsers can restore a previous SPA page from the back/forward cache
  // without re-running our onMount, so a logged-out user could land on
  // /captures via back-button with the old in-memory state. When that
  // happens, force a hard reload so the boot logic above runs again.
  function onPageShow(e) {
    if (e.persisted) window.location.reload();
  }

  function logout() {
    clearAuth();
    // Keep /captures (or wherever they were) in history. If the user hits
    // back, the boot logic above sees no token and redirects to /login --
    // that's the correct flow, not "remove the page from history."
    window.location.href = '/';
  }
</script>

<svelte:window onpageshow={onPageShow} />

{#if booting}
  <Splash />
{:else if user && user.has_access === false}
  <NoAccess {user} />
{:else}
  <UiHost />
  <Router>
    <Route path="/auth/callback"><Callback onAuthed={(u) => (user = u)} /></Route>
    <Route path="/login"><Login /></Route>

    {#if user}
      <!-- Logged in: everything lives inside the app shell. "/" redirects to
           Captures so there's no standalone home page rendering behind the
           shell. -->
      <AppShell {user} {logout}>
        <Route path="/"><HomeRedirect /></Route>
        <Route path="/captures"><Captures /></Route>
        <Route path="/captures/:id" let:params><CaptureDetail id={params.id} /></Route>
        <Route path="/buckets"><Buckets /></Route>
        <Route path="/buckets/:id" let:params><BucketDetail id={params.id} /></Route>
        <Route path="/users"><Users /></Route>
        <Route path="/audit"><Audit /></Route>
        <Route path="/logs"><Logs /></Route>
        <!-- Code-split: the import runs the first time /email-logs matches, not
             at app start, so the view's own JS and CSS stay out of the initial
             bundle. (highlight.js itself does not move -- CaptureDetail and Logs
             import it eagerly, so it stays in the main chunk.) -->
        <Route path="/email-logs">
          {#await import('./routes/EmailLogs.svelte')}
            <p class="text-on-surface-variant p-4">Loading email logs…</p>
          {:then module}
            <module.default />
          {:catch}
            <p class="text-error p-4">Couldn't load the email logs view. Reload the page to try again.</p>
          {/await}
        </Route>
        <Route path="/smokescreen"><Smokescreen /></Route>
        <Route path="/status"><Status /></Route>
        <Route path="/sftp"><Sftp /></Route>
        <!-- Bucket editor: "new" creates, any other id edits that bucket. -->
        <Route path="/sftp/:id" let:params><SftpBucketDetail id={params.id} /></Route>
        <Route path="/settings"><Settings /></Route>
      </AppShell>
    {:else}
      <!-- Logged out: the public landing with the sign-in CTA. -->
      <Route path="/"><Welcome {logout} /></Route>
    {/if}
  </Router>
{/if}
